/**
 * Forgiving fuzzy matching for catalog-style pickers — shared by the office's
 * `SearchSelect` autocomplete (client) and the agent's `findCatalog` tool
 * (server) so both find gear the same way: by model, sku, manufacturer, AND the
 * kind of thing it is (category / summary / tags).
 */

/**
 * Fold a string to a forgiving match key: lowercase, strip diacritics, map
 * Sony's branded `α` (the Alpha line) to `a`, and drop everything but letters
 * and digits. So "Sony α7 V (ilce-7m5)" → "sonya7vilce7m5", which a query of
 * "sony a7v" (folded to "sony","a7v") matches.
 */
export function foldKey(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u03b1/g, "a")
    .replace(/[^a-z0-9]+/g, "");
}

/**
 * Whether every whitespace-separated token of `query` appears (folded) somewhere
 * across `fields` — order-independent and diacritic/spacing/punctuation-
 * insensitive. An empty (or whitespace/symbol-only) query matches nothing; the
 * caller decides whether that means "show everything" or "show nothing".
 */
export function matchesSearch(
  query: string,
  fields: Array<string | null | undefined>,
): boolean {
  const tokens = query.split(/\s+/).map(foldKey).filter(Boolean);
  if (tokens.length === 0) return false;
  const key = fields.map((f) => foldKey(f ?? "")).join(" ");
  return tokens.every((t) => key.includes(t));
}
